import { Dialog as DialogPrimitive } from "radix-ui";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ReactNode,
} from "react";

import { cn } from "../../lib/cn";

import type { DialogSize } from "./types";

const sizeMap: Record<DialogSize, string> = {
  s: "w-120",
  m: "w-170",
  l: "w-270",
  xl: "w-270 max-h-[calc(100dvh-120px)]",
};

interface DialogContentProps extends ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  children: ReactNode;
  size?: DialogSize;
}

const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ children, size = "m", className, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        data-testid="dialog-overlay"
        className={cn(
          "fixed inset-0 bg-foreground/20 backdrop-blur-[10px]",
          "data-[state=closed]:animate-out data-[state=open]:animate-in",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:duration-[180ms] data-[state=open]:duration-[240ms]",
          "ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:animate-none",
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col",
          "max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto",
          "data-[state=closed]:animate-out data-[state=open]:animate-in",
          "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-2 data-[state=closed]:zoom-out-95",
          "data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:zoom-in-95",
          "data-[state=closed]:duration-[180ms] data-[state=open]:duration-[240ms]",
          "ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:animate-none",
          "focus-visible:outline-none",
          "rounded-2xl border border-border bg-white text-foreground shadow-[0px_2px_16px_rgba(0,0,0,0.08)]",
          sizeMap[size],
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export { DialogContent };
